import * as React from 'react';

import {
    F, Message, TabPanel, TabView, Tag, useConfirm, useDataTable, useDialogs, useForm,
    useLoading, useMessage, useRemoteData, useSearch, useTranslation
} from '@components';
import { IOption, ISecurity, ISubContractorRequest, IWork, ModuleManager, IUserIdentity } from '@models';
import DateUtils from '@utils/date-utils';
import Utils from '@utils';

type ProcessF = (workId: number, subContractorRequestId: number, reason?: string) => IOption<number>;

export interface IProps {
    acceptSubContractorRequest: ProcessF;
    getSubContractorRequestsAsWorker: Function;
    rejectSubContractorRequest: ProcessF;
    restrictInvitation: Function;
    getContractor: Function;
    getJob: Function;
    work: IWork;
    security: ISecurity;
    moduleManager: ModuleManager;
    user: IUserIdentity;
    deleteSubcontractorRequest: Function;
}

function RequestState({ request }: { request: ISubContractorRequest }) {
    const { t } = useTranslation();

    if (request.rejected) {
        return <span title={request.rejectedReason || undefined}><Tag value={t('Rejected request')} className='danger' /></span>;
    }
    else if (!request.acceptedByContractor && !request.acceptedByWork) {
        return <Tag value={t('Pending')}/>;
    }
    else if (request.acceptedByContractor) {
        return <Tag value={t('Accepted')} className='success' />;
    }
    else if (request.acceptedByWork) {
        return <Tag value={t('Accepted by main company')} className='info' />;
    }
    else {
        return null;
    }
}

function ProcessSubContractorRequest({
    accept, reject,
    onClose, onSuccess, request,
    workId
}: {
    accept: ProcessF, reject: ProcessF,
    onSuccess: Function,
    onClose: Function, request: ISubContractorRequest,
    workId: number,
}) {
    const { t } = useTranslation();
    const loading = useLoading();
    const message = useMessage();

    const form = useForm({
        initialValues: request,
    });

    const doAccept = async () => {
        loading.start();
        const res = await accept(workId, request.id!);
        loading.stop();
        message.set(res);

        if (res.hasValue) {
            onSuccess();
        }
    };

    const doReject = async () => {
        loading.start();
        const res = await reject(workId, request.id!, form.get<string>('rejectedReason'));
        loading.stop();
        message.set(res);

        if (res.hasValue) {
            onSuccess();
        }
    };

    return <div className='c'>
        <div className='c md pd'>
            <Message text={t('Process subcontractor request')}></Message>
            <div className='r v-center sm pd'>
                <strong className='sm pd-right'>{Utils.nameOf(request.sourceContractor)}</strong>
                <span>{t('Has requested {{name}} as subcontractor for the job', { name: Utils.nameOf(request.targetContractor)})}</span>
                <span className='sm pd-right pd-left'>{request.job?.name}</span>
                <Tag>{request.job?.code}</Tag>
            </div>
            <TabView >
                <TabPanel header={t('Accept')}>
                    <div className='r'>
                        <div className='e' />
                        <button disabled={loading.isLoading()} onClick={doAccept}>{t('Accept request')}</button>
                    </div>
                </TabPanel>
                <TabPanel header={t('Reject')}>
                    <div className='form-1 l200'>
                        <F label={t('Reason')}>
                            {form.textarea('rejectedReason')}
                        </F>
                        <div className='r'>
                            <div className='e' />
                            <button disabled={loading.isLoading()} onClick={doReject} className='danger'>{t('Rejected reason')}</button>
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </div>

        {message.render()}

        <div className='footer r'>
            {loading.render()}
            <div className='e' />
            <button onClick={() => onClose()}>{t('Cancel')}</button>
        </div>
    </div>
}

const renderRequestInfo = (r: ISubContractorRequest) => {
    if (r.rejected) {
        const d = DateUtils.formatFromUtc(r.rejectedDateTime);
        return <>
            <span title={d}>{d}</span>
        </>;
    }
    else if (r.acceptedByContractor) {
        const d = DateUtils.formatFromUtc(r.acceptedByContractorDateTime);
        return <>
            <span className='text-ellision' title={d}>{d}</span>
        </>;
    }
    else {
        return null;
    }
}

type ISubContractorRequestWithJob = ISubContractorRequest & {
    jobName?: string,
    jobCode?: string,
    token?: string,
    blacklisted?: boolean,
    departments: number[]
};

export function WorkSubContractorRequestsImpl(props: IProps) {
    const { t } = useTranslation();
    const dialogs = useDialogs();
    const [data, setData] = React.useState<ISubContractorRequestWithJob[]>([]);

    const prepareToProcessRequest = (r: ISubContractorRequest) => {
        dialogs.show('process', r);
    }

  const confirmActivate = useConfirm({
      message: t('Are you sure to delete the element ?'),
      accept: (r: any) => r.token !== undefined ? restrictInvitation(r) : deleteSubcontractorRequest(r),
    });

    const query = useRemoteData<ISubContractorRequest[]>(
        props.getSubContractorRequestsAsWorker,
        {
            parameters: [props.work.id],
        });

    const emailInvitationSearch = useSearch({
        workId: props.work.id,
        search: 'jobs/subcontractor.invitations.contractors',
        filters: {
            workId: props.work.id,
        },
        lazy: true,
    });

    const userInvitationsSearch = useSearch({
        workId: props.work.id,
        search: 'jobs/subcontractor.invitations.users',
        filters: {
            workId: props.work.id,
        },
        lazy: true,
    });

    const myRequests = useSearch({
        workId: props.work.id,
        search: 'jobs/subcontractor.requests.my',
        filters: {
            workId: props.work.id,
        },
        lazy: true,
    });

    const initialize = async () => {
        const req = await query.query();
        const emailInvitations = await emailInvitationSearch.doSearch();
        const userInvitations = await userInvitationsSearch.doSearch();
        const selfInvitations = props.security.isContractor()
            ? await myRequests.doSearch()
            : [];

        const combined: ISubContractorRequestWithJob[] = [
            ...req,
            ...(emailInvitations ?? []).map((invitation: any) => ({
                jobsDescription: invitation.description,
                jobId: invitation.jobId,
                jobName: invitation.jobName,
                jobCode: invitation.jobCode,
                shipmentNumber: invitation.shipmentNumber,
                sourceContractorId: invitation.sourceId,
                sourceContractor: invitation.sourceContractor,
                targetContractorId: 0,
                targetContractor: invitation.targetName,
                workId: props.work.id,
                dateTime: invitation.dateTime,
                acceptedByContractorDateTime: invitation.acceptedDate,
                rejected: invitation.accepted != null ? !invitation.accepted : false,
                acceptedByContractor: invitation.accepted,
                id: invitation.id,
                jobHasContractorId: invitation.jobHasContractorId,
                token: invitation.token,
                blacklisted: invitation.blacklisted,
                departments: invitation.departments
            })),
            ...(userInvitations ?? [])
                .filter((u: any) => u.jobId != null)
                .map((invitation: any) => ({
                    jobsDescription: invitation.description,
                    jobId: invitation.jobId,
                    jobName: invitation.jobName,
                    jobCode: invitation.jobCode,
                    shipmentNumber: invitation.shipmentNumber,
                    sourceContractorId: 0,
                    sourceContractor: invitation.source,
                    targetContractorId: 0,
                    targetContractor: invitation.contractorName,
                    dateTime: invitation.dateTime,
                    workId: props.work.id,
                    rejected: invitation.accepted != null ? !invitation.accepted : false,
                    acceptedByContractor: invitation.accepted,
                    acceptedByContractorDateTime: invitation.acceptedDate,
                    id: 0,
                    jobHasContractorId: invitation.jobHasContractorId,
                    blacklisted: invitation.blacklisted,
                    departments: invitation.departments
                })),
            ...selfInvitations
                .filter((u: any) => u.jobId != null),
            ];

        const seenIds = new Set<number>();
        const deduped = combined.filter(s => {
            if (s.id && seenIds.has(s.id)) return false;
            if (s.id) seenIds.add(s.id);
            return !s.blacklisted;
        });

        deduped.sort((a: any, b: any) => b.dateTime?.localeCompare(a.dateTime));

        const result = filter(deduped);

        setData(result);
    }

    React.useEffect(() => {
        initialize();
    }, []);

    const filter = (data: ISubContractorRequestWithJob[]) => {
        const ctx = {
            props: props,
        };
        const filterData = props
            .moduleManager
            .filterDependency('work.invitations', ctx);

        const result = data.filter(filterData);
        return result;
    }

    const date = (r: ISubContractorRequest) => {
        const d = DateUtils.formatFromUtc(r.dateTime!);
        return <div title={d}>
            <span>{d}</span>
        </div>
    };

    // const pessma = (r: ISubContractorRequestWithJob) => {
    //     return <div>
    //         <Tag value={jobCode(r)} />
    //         <span className='sm pd-left'>{jobName(r)}</span>
    //     </div>
    // }

    const sourceContractor = (r: ISubContractorRequest) => {
        const value = Utils.nameOf(r.sourceContractor);
        return <div title={value}>
            <span>{value}</span>
        </div>
    }

    const targetContractor = (r: ISubContractorRequest) => {
        const value = Utils.nameOf(r.targetContractor);
        return <div title={value}>
            {value}
        </div>
    };

    const state = (r: ISubContractorRequest) => {
        return <RequestState request={r} />;
    }

    const acceptedByContractor = (r: ISubContractorRequest) => {
        return renderRequestInfo(r);
    }

    const restrictInvitation = (r: ISubContractorRequestWithJob) => {
      props.restrictInvitation(r.token);
      refresh();
    }

    const deleteSubcontractorRequest = (r: ISubContractorRequestWithJob) => {
      props.deleteSubcontractorRequest(props.work.id, r.id);
      refresh();
    }

    const dataTable = useDataTable<ISubContractorRequestWithJob>({
        columns: [
            { title: t('Date'), sortKey: 'dateTime', className: 'td-md', render: (r: any) => date(r) },
            { title: t('Job'), sortKey: 'job.code', render: (r : any) => <div title={((r.job?.code ?? r.jobCode) + "|" + (r.job?.name ?? r.jobName))}><Tag value={r.job?.code ?? r.jobCode}/><span className='sm pd-left'>{r.job?.name ?? r.jobName}</span></div> },
            { title: t('Applicant'), sortKey: 'sourceContractor', render: (r: any) => sourceContractor(r) },
            { title: t('Requested contractor'), sortKey: 'targetContractor', render: (r: any) => targetContractor(r) },
            { title: t('State'), className: 'td-md', sortKey: 'accepted', render: (r: any) => state(r) },
            { title: t('Accepted'), className: 'td-md', sortKey: 'acceptedByContractor', render: (r: any) => acceptedByContractor(r) }
        ],
        actions: [
            { icon: 'play', onClick: prepareToProcessRequest, disabled: (r: any) => r.acceptedByContractor || r.acceptedByWork || r.rejected || r.id == 0 || props.security.isWorker() },
            { icon: 'pi pi-trash', onClick: (r: ISubContractorRequestWithJob) =>  confirmActivate(r) , disabled: (r: any) => !props.security.isGestor() || r.acceptedByContractor }
        ],
        data: data,
        loading: query.loading && emailInvitationSearch.loading && userInvitationsSearch.loading,
    });

    const refresh = () => {
        dialogs.clear();
        initialize();
    }

    return <div className='he c'>
        {dataTable()}

        {dialogs.render(
            'process',
            {},
            (data: ISubContractorRequest) =>
                <ProcessSubContractorRequest
                    accept={props.acceptSubContractorRequest}
                    reject={props.rejectSubContractorRequest}
                    workId={props.work.id}
                    onSuccess={refresh}
                    onClose={() => dialogs.clear()}
                    request={data} />)}
    </div>;
}

export function WorkSubContractorRequests(props: IProps) {
    const res = props
        .moduleManager
        .renderComponent<IProps>('WorkSubContractorRequests', { ...props }, WorkSubContractorRequestsImpl);
    return res;
}
